import { useState } from 'react';
import { Box, IconButton, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import { FilterAltOff, Search } from '@mui/icons-material';
import {
  CustomButton,
  CustomTextField,
  DrawerFormActionSummary,
  DrawerFormSection,
  GeneralTableBox,
  HorizontalForm,
  InfoTooltip,
  NoDataSkeleton,
  RowTableCell,
  SortableHeadTableCell,
} from '../../../common/components';
import { drawerFieldSpan } from '../../../common/styles';
import { useDebouncedValue } from '../../../common/hooks/useDebouncedValue';
import { useTableSort } from '../../../common/hooks/useTableSort';
import { formatDate } from '../../../utils/formatUtils';
import { sortRows } from '../../../utils/sortRows';
import { CONSOLIDATED_SUPPLY_TEXTS as TEXTS } from '../constants/consolidatedSupplyTexts';

import ClientPaginatedTable from '../../../common/components/table/ClientPaginatedTable';
/** Revisa los datos comunes y un documento por depósito antes de guardar. */
const ConsolidatedSupplySummary = ({ rows, providerLabel, commitment, onEditPurchase }) => {
  const [search, setSearch] = useState('');
  const query = useDebouncedValue(search).trim().toLowerCase();
  const { sort, toggleSort, resetSort } = useTableSort();
  const warehouses = [...new Set(rows.map((row) => row.uuid_destination_warehouse))];
  const documents = warehouses.map((id) => {
    const items = rows.filter((row) => row.uuid_destination_warehouse === id);
    return {
      uuid: id,
      warehouse_name: items[0].warehouse_name,
      lines: items.length,
      orders: new Set(items.map((row) => row.uuid_external_order)).size,
    };
  });
  const visible = sortRows(
    documents.filter((row) => row.warehouse_name.toLowerCase().includes(query)),
    sort
  );
  const columns = [
    { key: 'warehouse_name', label: TEXTS.warehouse },
    { key: 'orders', label: TEXTS.orders, width: 120 },
    { key: 'lines', label: TEXTS.lines, width: 120 },
  ];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      <DrawerFormSection
        title={TEXTS.summary}
        action={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CustomButton variant="text" onClick={onEditPurchase} sx={{ fontSize: 14, py: 0.5 }}>
              {TEXTS.editPurchase}
            </CustomButton>
            <InfoTooltip lines={[TEXTS.documentHint]} />
          </Box>
        }
      >
        <DrawerFormActionSummary
          label={TEXTS.provider}
          value={providerLabel || TEXTS.pendingProvider}
          sx={drawerFieldSpan(8)}
          valueSx={{ fontSize: 15, whiteSpace: 'normal', overflowWrap: 'anywhere' }}
        />
        <DrawerFormActionSummary
          label={TEXTS.commitment}
          value={commitment ? formatDate(commitment) : TEXTS.noCommitment}
          sx={drawerFieldSpan(4)}
          valueSx={{ fontSize: 15, whiteSpace: 'normal' }}
        />
      </DrawerFormSection>
      <Box>
        <HorizontalForm withBorder={false} sx={{ px: 2, py: 1.5, gap: 1, alignItems: 'center' }}>
          <CustomTextField
            label={TEXTS.searchDocuments}
            value={search}
            sx={{ flex: '1 1 260px', minWidth: 0, mt: 0 }}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{ startAdornment: <Search sx={{ color: 'primary.main', mr: 1 }} /> }}
          />
          <Tooltip title={TEXTS.clearFilter}>
            <IconButton
              color="primary"
              aria-label={TEXTS.clearFilter}
              onClick={() => {
                setSearch('');
                resetSort();
              }}
            >
              <FilterAltOff />
            </IconButton>
          </Tooltip>
        </HorizontalForm>
        <ClientPaginatedTable rows={visible} resetKey={`${query}:${sort?.key}:${sort?.direction}`}>
          {(pageRows) => (
            <GeneralTableBox fixedLayout minTableWidth={550} hasMaxHeight={false}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <SortableHeadTableCell
                        key={column.key}
                        sortKey={column.key}
                        sort={sort}
                        onSort={toggleSort}
                        sx={{ width: column.width, textAlign: column.width ? 'right' : 'left' }}
                      >
                        {column.label}
                      </SortableHeadTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((row) => (
                    <TableRow hover key={row.uuid}>
                      {columns.map((column) => (
                        <RowTableCell
                          key={column.key}
                          sx={{ textAlign: column.width ? 'right' : 'left', fontSize: 13 }}
                          title={String(row[column.key])}
                        >
                          {row[column.key]}
                        </RowTableCell>
                      ))}
                    </TableRow>
                  ))}
                  {!visible.length && (
                    <NoDataSkeleton
                      colSpan={3}
                      showSubtitle={false}
                      message={query ? TEXTS.noMatches : TEXTS.empty}
                    />
                  )}
                </TableBody>
              </Table>
            </GeneralTableBox>
          )}
        </ClientPaginatedTable>
      </Box>
    </Box>
  );
};

export default ConsolidatedSupplySummary;
